/**
 * The Agreement's commercial side (piece 29; decisions 0096, 0154, 0733): when
 * a contract is active, when its schedule bills, and which Agreement covers a
 * job. Pure, so the generator, the coverage question and every report read one
 * definition. Days are ISO dates, `YYYY-MM-DD`, already in the organisation's
 * zone; nothing here reads a clock.
 */
import { zeroPad } from '../zonedTime';


export const AGREEMENT_TYPES = ['Retainer', 'ServiceContract', 'MasterProject'] as const;
export type AgreementTypeValue = (typeof AGREEMENT_TYPES)[number];

export const AGREEMENT_CADENCES = ['Monthly', 'Quarterly', 'Annually'] as const;
export type AgreementCadenceValue = (typeof AGREEMENT_CADENCES)[number];

/** Whole months in one period of each cadence. */
export const AGREEMENT_CADENCE_MONTHS: Readonly<Record<AgreementCadenceValue, number>> = { Monthly: 1, Quarterly: 3, Annually: 12 };

/** 🔨 `dayOfPeriod` is capped at 28, so February is never a special case. */
export const MAX_DAY_OF_PERIOD = 28;

/** The daily job on the job clock (decisions 0096, 0733). */
export const GENERATE_SCHEDULED_INVOICES_JOB = 'generateScheduledInvoices';
export const GENERATE_SCHEDULED_INVOICES_HOUR_KEY = 'agreements.generateScheduledInvoicesRunHour';
export const GENERATE_SCHEDULED_INVOICES_DEFAULT_HOUR = 6;

/** The to-do winning a Deal raises, and the screen it opens (decision 0735). */
export const AGREEMENT_COVERAGE_KIND = 'AgreementCoverageQuestion' as const;
export const AGREEMENT_COVERAGE_SOURCE = 'AgreementCoverage';
export function agreementCoverageRoute(dealId: string): string {
  return `/work/coverage/${dealId}`;
}

/** The three answers the coverage screen takes. */
export const COVERAGE_ANSWERS = ['Agreement', 'Expansion', 'Neither'] as const;
export type CoverageAnswer = (typeof COVERAGE_ANSWERS)[number];

export interface AgreementDates {
  startDate: string;
  endDate: string | null;
  /** A reminder and never a gate. */
  renewalDate?: string | null;
}

/**
 * 🔨 Active on a day when `startDate` is on or before it and `endDate?` is
 * absent or on or after it. `renewalDate?` is read by nothing here: a contract
 * past its renewal date still bills until its end date arrives.
 */
export function agreementActiveOn(agreement: AgreementDates, day: string): boolean {
  return agreement.startDate <= day && (agreement.endDate === null || agreement.endDate >= day);
}

export function dayOfPeriodProblem(day: number): string | null {
  if (!Number.isInteger(day) || day < 1) return 'the billing day is a whole day of the period, 1 or later';
  if (day > MAX_DAY_OF_PERIOD) return `the billing day is capped at ${MAX_DAY_OF_PERIOD}, so February is never a special case`;
  return null;
}

export interface ScheduleRowLike {
  id: string;
  cadence: AgreementCadenceValue;
  /** Integer cents. */
  amount: number;
  dayOfPeriod: number;
  firstBillingDate: string;
  lastBillingDate: string | null;
}

function monthIndex(day: string): number {
  const [y, m] = day.split('-').map(Number) as [number, number];
  return y * 12 + (m - 1);
}
function dayIn(index: number, dayOfPeriod: number): string {
  return `${Math.floor(index / 12)}-${zeroPad((index % 12) + 1)}-${zeroPad(dayOfPeriod)}`;
}

/**
 * Every billing date a row reaches up to `through`: `firstBillingDate` plus
 * whole cadence periods, on `dayOfPeriod`, never before the first date and
 * never past `lastBillingDate?`. Derived each time, so no cursor column can
 * fall behind the invoices.
 */
export function billingDatesOf(row: Pick<ScheduleRowLike, 'cadence' | 'dayOfPeriod' | 'firstBillingDate' | 'lastBillingDate'>, through: string): string[] {
  const step = AGREEMENT_CADENCE_MONTHS[row.cadence];
  const start = monthIndex(row.firstBillingDate);
  const out: string[] = [];
  for (let k = 0; k < 1200; k++) {
    const date = dayIn(start + k * step, row.dayOfPeriod);
    if (date < row.firstBillingDate) continue;
    if (row.lastBillingDate !== null && date > row.lastBillingDate) break;
    if (date > through) break;
    out.push(date);
  }
  return out;
}

export interface DuePeriod {
  scheduleId: string;
  /** The billing date, which is the period's start and half of the idempotency key. */
  periodStart: string;
  amount: number;
}

/**
 * The periods the generator raises today: every billing date on or before
 * today, on a day the Agreement is active, that no Invoice already carries for
 * this Agreement. 🔨 A period already raised is skipped by its stored key, never
 * by a cursor, so a re-run raises nothing twice.
 */
export function duePeriods(rows: readonly ScheduleRowLike[], agreement: AgreementDates, today: string, raised: ReadonlySet<string>): DuePeriod[] {
  const out: DuePeriod[] = [];
  for (const row of rows) {
    for (const date of billingDatesOf(row, today)) {
      if (!agreementActiveOn(agreement, date) || raised.has(date)) continue;
      out.push({ scheduleId: row.id, periodStart: date, amount: row.amount });
    }
  }
  return out.sort((a, b) => a.periodStart.localeCompare(b.periodStart));
}

/** The next billing date after today, for the Agreement's page; null where nothing more bills. */
export function nextBillingDate(rows: readonly ScheduleRowLike[], agreement: AgreementDates, today: string): string | null {
  const horizon = `${Number(today.slice(0, 4)) + 2}${today.slice(4)}`;
  const upcoming = rows
    .flatMap((row) => billingDatesOf(row, horizon))
    .filter((date) => date > today && agreementActiveOn(agreement, date))
    .sort();
  return upcoming[0] ?? null;
}

/** The day before an ISO date. */
export function previousDay(day: string): string {
  const at = new Date(`${day}T00:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() - 1);
  return at.toISOString().slice(0, 10);
}

export interface ScheduleChange {
  cadence: AgreementCadenceValue;
  amount: number;
  dayOfPeriod: number;
  effectiveFrom: string;
}

/**
 * 🔨 A price change closes one row and opens the next. The current row takes
 * `lastBillingDate?` the day before the change, and the new row opens on it,
 * so an invoice already raised is never re-rated.
 */
export function scheduleChangeProblem(current: ScheduleRowLike | null, change: ScheduleChange): string | null {
  const day = dayOfPeriodProblem(change.dayOfPeriod);
  if (day) return day;
  if (!Number.isInteger(change.amount) || change.amount < 0) return 'the amount is a sum of money, nothing below zero';
  if (!(AGREEMENT_CADENCES as readonly string[]).includes(change.cadence)) return `${change.cadence} is not a cadence; the cadences are ${AGREEMENT_CADENCES.join(', ')}`;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(change.effectiveFrom)) return 'the change takes effect on a date';
  if (current && change.effectiveFrom <= current.firstBillingDate) return `the new amount takes effect after ${current.firstBillingDate}, the day the current row opened`;
  return null;
}

// ---- Coverage ----

export interface CoverageAgreement extends AgreementDates {
  id: string;
  coveredSiteIds: readonly string[];
  coveredAssetNodeIds: readonly string[];
}

export interface CoverageQuestion {
  /** The job's node first, then each ancestor up to the top; empty where the job names no node. */
  nodeAncestry: readonly string[];
  siteId: string | null;
}

/**
 * 🔨 A node is covered when it or any ancestor appears in
 * `coveredAssetNodeIds[]`, or when the job's Site appears in `coveredSiteIds[]`.
 * The nearest node match wins, then the Site; the database refuses two active
 * Agreements over one node or one Site, so each axis resolves to one.
 */
export function coveringAgreement(agreements: readonly CoverageAgreement[], question: CoverageQuestion, day: string): { agreementId: string; via: 'node' | 'site' } | null {
  const active = agreements.filter((a) => agreementActiveOn(a, day));
  for (const nodeId of question.nodeAncestry) {
    const hit = active.find((a) => a.coveredAssetNodeIds.includes(nodeId));
    if (hit) return { agreementId: hit.id, via: 'node' };
  }
  if (question.siteId) {
    const hit = active.find((a) => a.coveredSiteIds.includes(question.siteId!));
    if (hit) return { agreementId: hit.id, via: 'site' };
  }
  return null;
}

/** Whether two Agreements are both active on some day. */
export function activeRangesMeet(a: AgreementDates, b: AgreementDates): boolean {
  return a.startDate <= (b.endDate ?? '9999-12-31') && b.startDate <= (a.endDate ?? '9999-12-31');
}

/**
 * The Agreement a candidate's coverage would clash with, or null: one whose
 * active range meets the candidate's and that names one of its Sites, or one
 * of its nodes, an ancestor of one, or a descendant of one. `ancestry` answers
 * a node's chain upward, the node first.
 */
export function coverageClash(candidate: CoverageAgreement, others: readonly CoverageAgreement[], ancestry: (nodeId: string) => readonly string[]): string | null {
  const mine = new Set(candidate.coveredAssetNodeIds.flatMap((id) => ancestry(id)));
  for (const other of others) {
    if (other.id === candidate.id || !activeRangesMeet(candidate, other)) continue;
    if (other.coveredSiteIds.some((id) => candidate.coveredSiteIds.includes(id))) return other.id;
    if (other.coveredAssetNodeIds.some((id) => mine.has(id))) return other.id;
    const theirs = new Set(other.coveredAssetNodeIds.flatMap((id) => ancestry(id)));
    if (candidate.coveredAssetNodeIds.some((id) => theirs.has(id))) return other.id;
  }
  return null;
}

// ---- Included hours (decision 3901; T1160) ----

/** A period an Agreement's included hours are drawn in, its first and last day inclusive. */
export interface IncludedPeriod {
  start: string;
  end: string;
}

/** The day `startDate` falls on in `year`; a 29 February start falls on the 28th in a year without one. */
function anniversaryIn(startDate: string, year: number): string {
  const [, month, dayOfMonth] = startDate.split('-').map(Number) as [number, number, number];
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${String(year).padStart(4, '0')}-${zeroPad(month)}-${zeroPad(Math.min(dayOfMonth, last))}`;
}

/**
 * 🔒 Decision 3901: the period a work date draws its included hours in. It is
 * the billing schedule's cadence period that contains the day: from the row's
 * billing date on or before it to the day before the row's next one, never
 * past the row's own last day. A day no schedule row covers, and every day of
 * an Agreement with no row, falls in the contract year counted from the start.
 */
export function includedHoursPeriod(rows: readonly Pick<ScheduleRowLike, 'cadence' | 'dayOfPeriod' | 'firstBillingDate' | 'lastBillingDate'>[], startDate: string, day: string): IncludedPeriod {
  const row = [...rows]
    .filter((r) => r.firstBillingDate <= day && (r.lastBillingDate === null || day <= r.lastBillingDate))
    .sort((a, b) => b.firstBillingDate.localeCompare(a.firstBillingDate))[0];
  if (row) {
    const start = billingDatesOf(row, day).at(-1) ?? row.firstBillingDate;
    const horizon = dayIn(monthIndex(start) + AGREEMENT_CADENCE_MONTHS[row.cadence] + 1, 28);
    const next = billingDatesOf(row, horizon).find((date) => date > start);
    const end = next ? previousDay(next) : row.lastBillingDate ?? previousDay(dayIn(monthIndex(start) + AGREEMENT_CADENCE_MONTHS[row.cadence], row.dayOfPeriod));
    return { start, end: row.lastBillingDate !== null && row.lastBillingDate < end ? row.lastBillingDate : end };
  }
  let year = Number(day.slice(0, 4));
  if (anniversaryIn(startDate, year) > day) year -= 1;
  const start = day < startDate ? startDate : anniversaryIn(startDate, year);
  const from = Number(start.slice(0, 4));
  return { start, end: previousDay(anniversaryIn(startDate, from + 1)) };
}

/** One stretch of covered labor a draw reads: whose it is, the day it was worked and its hours. */
export interface IncludedHoursCandidate {
  key: string;
  workDate: string;
  hours: number;
}

export interface IncludedHoursDraw {
  /** The hours drawn for each key, summed over its days. */
  drawn: Map<string, number>;
  /** The hours drawn in each period, by its start, with the period. */
  byPeriod: Map<string, { period: IncludedPeriod; hours: number }>;
}

const hundredths = (hours: number): number => Math.round(hours * 100);

/**
 * 🔒 Decision 3901: covered labor draws against each period's included hours
 * in work-date order, oldest first, after what the period's earlier invoices
 * already drew. A day's hours draw in the period that contains the day, so a
 * job worked across two periods draws each part against its own. Hours past
 * what a period has left bill at the resolved rate. Arithmetic runs in whole
 * hundredths, the line's own scale.
 */
export function drawIncludedHours(input: { allowance: number; usedBefore: ReadonlyMap<string, number>; periodOf: (day: string) => IncludedPeriod; candidates: readonly IncludedHoursCandidate[] }): IncludedHoursDraw {
  const drawn = new Map<string, number>();
  const byPeriod = new Map<string, { period: IncludedPeriod; hours: number }>();
  const allowance = hundredths(input.allowance);
  if (!(allowance > 0)) return { drawn, byPeriod };
  const ordered = input.candidates.map((candidate, at) => ({ candidate, at })).sort((a, b) => a.candidate.workDate.localeCompare(b.candidate.workDate) || a.at - b.at);
  for (const { candidate } of ordered) {
    const want = hundredths(candidate.hours);
    if (want <= 0) continue;
    const period = input.periodOf(candidate.workDate);
    const taken = byPeriod.get(period.start);
    const left = allowance - hundredths(input.usedBefore.get(period.start) ?? 0) - hundredths(taken?.hours ?? 0);
    const take = Math.min(left, want);
    if (take <= 0) continue;
    drawn.set(candidate.key, (hundredths(drawn.get(candidate.key) ?? 0) + take) / 100);
    byPeriod.set(period.start, { period, hours: (hundredths(taken?.hours ?? 0) + take) / 100 });
  }
  return { drawn, byPeriod };
}

// ---- The renewal reminder (decision 3902; T1161) ----

/** The Agreement as the reminder engine's date-mode subject, anchored on its renewal date. */
export const AGREEMENT_SUBJECT = 'Agreement';
/** The To-Do the source Deal's owner gets before an Agreement renews, and the record it opens. */
export const AGREEMENT_RENEWAL_KIND = 'AgreementRenewalDue' as const;
export function agreementRoute(agreementId: string): string {
  return `/work/agreements/${agreementId}`;
}

/** 🔒 How many days before the renewal date the To-Do is raised, seeded at 30 and edited on Settings › Service › Dispatch Rules. */
export const AGREEMENT_RENEWAL_DAYS_KEY = 'agreements.renewalReminderDays';
export const AGREEMENT_RENEWAL_DAYS_DEFAULT = 30;
export const AGREEMENT_RENEWAL_DAYS_MAX = 365;

/** A whole number of days from 1 to 365, never blank, because the reminder reads it on every run. */
export function renewalReminderDaysProblem(value: unknown): string | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= AGREEMENT_RENEWAL_DAYS_MAX
    ? null
    : `the renewal reminder is a whole number of days from 1 to ${AGREEMENT_RENEWAL_DAYS_MAX}`;
}

/** The setting as read: a stored whole number in range, else the seeded 30. */
export function renewalReminderDaysOf(value: unknown): number {
  return renewalReminderDaysProblem(value) === null ? (value as number) : AGREEMENT_RENEWAL_DAYS_DEFAULT;
}

/**
 * The renewal reminder as a ReminderRule row: date mode on `renewalDate`, in
 * the app, to the organisation's own people. 🔨 Its offsets stay empty: the
 * lead is the Settings row, which the resolver reads, because the engine reads
 * a rule's offsets and never a Settings key (decision 3902).
 */
export const AGREEMENT_RENEWAL_RULE = {
  subjectType: AGREEMENT_SUBJECT,
  anchorField: 'renewalDate',
  offsets: [] as number[],
  intervalDays: null,
  intervalSourceField: null,
  audience: 'Internal' as const,
  channel: 'InApp' as const,
  maxSends: 1,
  stopCondition: 'SubjectClosed' as const,
  businessDaysOnly: false,
} as const;

/** The template the rule names, which the engine picks by documentType; the reminder sends no mail. */
export const AGREEMENT_RENEWAL_TEMPLATE = {
  name: 'Agreement renewal',
  documentType: AGREEMENT_SUBJECT,
  subject: 'The Agreement with {{companyName}} renews on {{renewalDate}}',
  bodyRichText: '<p>The Agreement with {{companyName}} renews on {{renewalDate}}. Agree the next term and move the renewal date, or set the end date if it is not renewing.</p>',
} as const;

function dayAfter(day: string, days: number): string {
  const at = new Date(`${day}T00:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/**
 * 🔒 The To-Do is owed from `leadDays` before the renewal date through the
 * renewal date itself, on an Agreement that does not end before it renews.
 * It is raised once per Agreement per renewal date; the service keys that.
 */
export function renewalReminderDue(agreement: { renewalDate: string | null; endDate: string | null }, today: string, leadDays: number): boolean {
  const renews = agreement.renewalDate;
  if (renews === null) return false;
  if (agreement.endDate !== null && agreement.endDate < renews) return false;
  return today >= dayAfter(renews, -leadDays) && today <= renews;
}

/** 🔒 A To-Do raised for `remindedFor` is settled once the renewal date moves, or the Agreement ends before that date. */
export function renewalReminderSettled(agreement: { renewalDate: string | null; endDate: string | null }, remindedFor: string): boolean {
  return agreement.renewalDate !== remindedFor || (agreement.endDate !== null && agreement.endDate < remindedFor);
}
